import { frappeList, frappeGet } from "./utils";
import { getERPClient } from "./client";
import type { FrappeListResponse } from "@/lib/frappe-client";
import type { FrappeFilterArray } from "./filterMapper";
import { docTypeListWithPagination } from "./docs";

/**
 * Default fields to fetch for Stock Entry list views
 *
 * This constant ensures consistency across:
 * - stockEntries.list() (legacy, if needed)
 * - stockEntries.listWithPagination() defaultFields
 * - useStockEntryListWithPagination fields
 * - createStockEntryColumns accessorKeys
 *
 * IMPORTANT:
 * - If you add a new column to createStockEntryColumns, add the corresponding field here.
 * - Note: "modified" can be used for sorting even if not included here. If you add a
 *   "Last Updated" column later, add "modified" to this array.
 */
export const STOCK_ENTRY_LIST_FIELDS = [
  "name",
  "posting_date",
  "posting_time",
  "stock_entry_type",
  "from_warehouse",
  "to_warehouse",
  "company",
  // NOTE: total_qty and total_amount are NOT real columns on Stock Entry
  // They would need to be computed from Stock Entry Detail (child table) via a custom backend method
  // "total_qty",
  // "total_amount",
  "docstatus",  // Real field: 0=Draft, 1=Submitted, 2=Cancelled
  "remarks",
] as const;

export interface StockEntryListParams {
  /**
   * Pre-mapped Frappe filter array, e.g.:
   * [["Stock Entry", "stock_entry_type", "=", "Material Transfer"]]
   */
  filters?: FrappeFilterArray;
  pageIndex?: number;
  pageSize?: number;
  orderBy?: string;
  fields?: string[];
}

export type StockEntry = {
  name: string;
  posting_date: string;
  posting_time?: string;
  stock_entry_type: string;
  from_warehouse?: string;
  to_warehouse?: string;
  docstatus: 0 | 1 | 2;  // 0=Draft, 1=Submitted, 2=Cancelled
  company?: string;
  // NOTE: total_qty and total_amount are NOT real columns on Stock Entry
  // They would need to be computed from Stock Entry Detail (child table) via a custom backend method
  // total_qty?: number;
  // total_amount?: number;
  remarks?: string;
  // Allow additional backend fields without breaking typing
  [key: string]: any;
};

export const stockEntries = {
  /**
   * List all stock entries (legacy method - returns array only)
   * @deprecated Use listWithPagination for server-side pagination
   */
  list(filters?: Record<string, any>): Promise<StockEntry[]> {
    return frappeList<StockEntry>(
      "Stock Entry",
      filters,
      [...STOCK_ENTRY_LIST_FIELDS]
    );
  },

  /**
   * List stock entries with server-side pagination and filtering.
   *
   * This currently uses the generic docTypeListWithPagination path without
   * any special logic. If you later need Stock Entry–specific backend
   * behavior, you can add it here or use docTypeListWithPaginationViaMethod.
   */
  listWithPagination(
    params: StockEntryListParams
  ): Promise<FrappeListResponse<StockEntry>> {
    const {
      filters = [],
      pageIndex = 0,
      pageSize = 20,
      orderBy,
      fields,
    } = params;

    const defaultFields = fields ?? [...STOCK_ENTRY_LIST_FIELDS];

    return docTypeListWithPagination<StockEntry>({
      doctype: "Stock Entry",
      filters,
      pageIndex,
      pageSize,
      orderBy: orderBy ?? "posting_date desc",
      fields: defaultFields,
    });
  },

  /**
   * Get a single stock entry by name
   */
  detail(name: string): Promise<StockEntry> {
    return frappeGet<StockEntry>("Stock Entry", name);
  },

  /**
   * Create a new stock entry
   */
  create(data: Record<string, any>): Promise<StockEntry> {
    const client = getERPClient();
    return client.create({ doctype: "Stock Entry", data });
  },

  /**
   * Update an existing stock entry
   */
  update(name: string, data: Record<string, any>): Promise<StockEntry> {
    const client = getERPClient();
    return client.update({ doctype: "Stock Entry", name, data });
  },

  /**
   * Delete a stock entry
   */
  delete(name: string): Promise<void> {
    const client = getERPClient();
    return client.delete({ doctype: "Stock Entry", name });
  },

  /**
   * Get basic rate (as per Stock UOM) for quick action modal
   * Uses the same ERPNext logic as the Stock Entry form
   */
  async getQuickActionBasicRate(params: {
    item_code: string;
    stock_entry_type: string;
    warehouse?: string;
    company?: string;
    qty?: number;
  }): Promise<{ basic_rate: number }> {
    const client = getERPClient();
    return client.call<{ basic_rate: number }>(
      "zaviago_backend.api.stock_entry.get_quick_action_basic_rate",
      params
    );
  },
};

